import { useState } from "react";
import { Heart, Sparkles, Shield, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * In-run Perk Draft (iter67) — opens after every boss clear.
 * Shows 3 perk cards; player picks ONE which persists for the rest of this run.
 * The backend returns the updated run state (max_health, health, active_perks);
 * caller's `onPicked(payload)` merges those into local run state.
 */
const ICONS = { heart: Heart, sparkles: Sparkles, shield: Shield };

export default function PerkDraftModal({ open, perks = [], onPicked }) {
  const [busyKey, setBusyKey] = useState(null);

  if (!open || !perks.length) return null;

  const pick = async (perk) => {
    if (busyKey) return;
    setBusyKey(perk.key);
    try {
      const res = await api.post("/game/perk/pick", { perk_key: perk.key });
      toast.success(`${perk.name} — carried for the rest of this run.`);
      onPicked && onPicked(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not claim perk");
      setBusyKey(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center px-4"
      style={{ background: "rgba(5,2,1,0.92)" }}
      data-testid="perk-draft-modal"
    >
      <div className="max-w-3xl w-full">
        <div className="text-center mb-6">
          <div className="text-[10px] tracking-[0.45em] ash-text">BOSS · FELLED</div>
          <h2 className="font-heading text-3xl sm:text-4xl text-[#f0e6d2] italic mt-2">
            The Ember Offers a Boon
          </h2>
          <p className="text-xs ash-text italic mt-2">Choose one — it carries for the rest of this descent.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          {perks.map((p) => {
            const Icon = ICONS[p.icon] || Sparkles;
            const busy = busyKey === p.key;
            return (
              <button
                key={p.key}
                type="button"
                disabled={!!busyKey}
                onClick={() => pick(p)}
                data-testid={`perk-card-${p.key}`}
                className="panel p-5 text-left transition-all duration-200 hover:border-[#d4af37] hover:scale-[1.02] disabled:opacity-60 disabled:cursor-wait"
                style={{
                  background: busy
                    ? "radial-gradient(ellipse at top, rgba(90,20,10,0.4), rgba(10,5,3,0.98))"
                    : "radial-gradient(ellipse at top, rgba(40,25,10,0.3), rgba(10,5,3,0.98))",
                  minHeight: 180,
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  {busy ? <Loader2 size={20} className="gold-text animate-spin" /> : <Icon size={20} className="gold-text" />}
                  <span className="text-[9px] tracking-[0.4em] ash-text">BOON</span>
                </div>
                <div className="font-heading text-xl text-[#f0e6d2] leading-tight">{p.name}</div>
                <div className="mt-3 h-px bg-gradient-to-r from-[#d4af37]/50 to-transparent" />
                <p className="mt-3 text-sm text-[#f0e6d2] italic leading-relaxed">{p.desc}</p>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
